// PowerComposer — the Send/Stop/Retry state machine, pure.
//
// Research finding (.claude/analysis/chat-composer-research.md §5, §7.1): a
// premium composer swaps the send button's whole IDENTITY as a turn moves
// idle → ready → streaming → error, rather than greying one button out.
// Promoted from sketches/chat-composer/SendStateButton.tsx's
// `SketchComposerPhase`, split into two named sums per this project's
// "distinct commands per intent":
//
// - `PowerComposerStatus` is what the CALLER (the turn pipeline) tracks and
//   passes in — a turn is idle, streaming, or failed. It has no "ready"
//   variant because the pipeline has no opinion on whether the draft is
//   non-empty; that is the composer's own business.
// - `SendState` is what the SEND BUTTON actually renders — the same three
//   pipeline states PLUS "ready", derived by folding in whether the current
//   draft has content. Never constructed directly by a caller; always via
//   `deriveSendState`.
//
// Keeping these as two sums (rather than one four-variant sum the caller
// must also populate correctly) means a caller cannot accidentally pass
// `{ type: "ready" }` while the draft is empty — that state is simply
// unconstructible from the outside.

import { exhaustSwitchCase } from "@bilda/utils";

export type PowerComposerStatus =
    | { readonly type: "idle" } // no turn in flight — Send is available once there's a draft
    | { readonly type: "streaming" } // a reply is arriving — Stop is live, input stays editable
    | { readonly type: "error"; readonly message: string } // last turn failed — Retry is live
    // G3-interim-B1: the last turn was interrupted, but it had a material build
    // underway — and per-step commit means that course almost certainly SURVIVED
    // (.claude/analysis/g3-atomic-generation-rootcause.md §3). So we DEMOTE the blind
    // Retry (which would re-run the whole turn and double-create the material) to
    // a calm "your course was created — review it, then continue" note, and the
    // send button reverts to a plain Send (never a Retry). No message needed —
    // the composer renders the fixed reassurance inline.
    | { readonly type: "created" };

// PS-61: Send is always Send, never Stop. While a turn streams, a message
// sent waits in the thread for the assistant's next step; Stop lives on the
// running step instead.
export type SendState =
    | { readonly type: "idle" } // no draft yet — Send exists but is inert
    | { readonly type: "ready" } // draft present — Send is live
    | { readonly type: "error"; readonly message: string };

export const deriveSendState = (
    status: PowerComposerStatus,
    hasDraft: boolean,
): SendState => {
    switch (status.type) {
        case "error":
            return { type: "error", message: status.message };
        // The "created" demotion (G3-interim-B1) is NOT a send-button identity —
        // the button must NOT become a Retry (that would re-fire the turn). It is
        // a plain Send, exactly like idle, so the author continues rather than
        // re-triggering; the reassurance note is rendered elsewhere in the frame.
        case "created":
            return hasDraft ? { type: "ready" } : { type: "idle" };
        case "idle":
        case "streaming":
            return hasDraft ? { type: "ready" } : { type: "idle" };
        default:
            return exhaustSwitchCase(status);
    }
};
